import { Button as AntDesignButton, GetRef, Tooltip } from 'antd';
import cx from 'classnames';
import React from 'react';

import styles from './_Button.module.scss';

type IButtonProps = {
  onClick?: () => void;
  type:
    | 'primary'
    | 'outline'
    | 'destructive'
    | 'link'
    | 'linkNeutral'
    | 'outlineNeutral'
    | 'userMenu';
  size?: 'middle' | 'large';
  iconPosition?: 'start' | 'end';
  label?: string;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  circle?: boolean;
  tooltipText?: string;
  tooltipPosition?:
    | 'top'
    | 'left'
    | 'right'
    | 'bottom'
    | 'topLeft'
    | 'topRight'
    | 'bottomLeft'
    | 'bottomRight'
    | 'leftTop'
    | 'leftBottom'
    | 'rightTop'
    | 'rightBottom';
  submit?: boolean;
  ReactIconClass?: React.ElementType;
  className?: string;
  active?: boolean;
};

const getPrimaryClass = (
  type: string,
  loading?: boolean,
  disabled?: boolean,
) => {
  if (type !== 'primary') return null;
  if (disabled) return styles.buttonDisabledClass;
  if (loading) return styles.buttonPrimaryLoadingClass;
  return styles.buttonPrimaryClass;
};

const getLinkClass = (type: string, loading?: boolean, disabled?: boolean) => {
  if (type !== 'link') return null;
  if (disabled) return styles.buttonDisabledClass;
  if (loading) return styles.buttonLinkLoadingClass;

  return styles.buttonLinkClass;
};

const getLinkNeutralClass = (
  type: string,
  loading?: boolean,
  disabled?: boolean,
  active?: boolean,
) => {
  if (type !== 'linkNeutral') return null;
  if (disabled) return styles.buttonDisabledClass;
  if (loading) return styles.buttonLinkNeutralLoadingClass;
  if (active) return styles.buttonLinkNeutralActiveClass;
  return styles.buttonLinkNeutralClass;
};

const getDestructiveClass = (
  type: string,
  loading?: boolean,
  disabled?: boolean,
) => {
  if (type !== 'destructive') return null;
  if (disabled) return styles.buttonDisabledClass;
  if (loading) return styles.buttonDestructiveLoadingClass;
  return styles.buttonDestructiveClass;
};

const getOutlineClass = (
  type: string,
  loading?: boolean,
  disabled?: boolean,
  active?: boolean,
) => {
  if (type !== 'outline' && type !== 'outlineNeutral') return null;
  if (disabled) return styles.buttonDisabledClass;
  if (loading) return styles.buttonPrimaryLoadingClass;
  if (active) return styles.buttonOutlineActiveClass;
  return styles.buttonOutlineClass;
};

const getUserMenuButtonClass = (
  type: string,
  loading?: boolean,
  disabled?: boolean,
) => {
  if (type !== 'userMenu') return null;
  if (disabled) return styles.buttonDisabledClass;
  if (loading) return styles.buttonPrimaryLoadingClass;

  return styles.buttonUserMenuClass;
};

const getButtonType = (type: string) => {
  switch (type) {
    case 'link':
      return 'link';
    case 'linkNeutral':
      return 'link';
    case 'primary':
      return 'primary';
    case 'destructive':
      return 'primary';
    case 'outline':
      return undefined;
    case 'userMenu':
      return undefined;
    case 'outlineNeutral':
      return undefined;
    default:
      return undefined;
  }
};
const Button = React.forwardRef<GetRef<typeof AntDesignButton>, IButtonProps>(
  (
    {
      onClick,
      type,
      size,
      label,
      loading,
      disabled,
      fullWidth,
      iconPosition,
      ReactIconClass,
      circle,
      tooltipText,
      submit,
      active,
      className = '',
      tooltipPosition = 'top',
    },
    ref,
  ) => (
    <Tooltip title={tooltipText} placement={tooltipPosition}>
      <AntDesignButton
        ref={ref}
        type={getButtonType(type)}
      size={size}
      onClick={onClick}
      loading={loading}
      disabled={disabled}
      shape={circle ? 'circle' : 'default'}
      htmlType={submit ? 'submit' : undefined}
      className={cx(
        { 'tw-text-[--color-text-secondary]': type !== 'primary' },
        'tw-flex tw-items-center tw-justify-center tw-gap-2',
        'tw-shadow-none tw-border tw-border-solid tw-border-transparent tw-px-4 tw-rounded-xl tw-font-medium',
        getPrimaryClass(type, loading, disabled),
        getLinkClass(type, loading, disabled),
        getLinkNeutralClass(type, loading, disabled, active),
        getDestructiveClass(type, loading, disabled),
        getOutlineClass(type, loading, disabled, active),
        getUserMenuButtonClass(type),
        className,
        {
          '!tw-border !tw-border-solid !tw-bg-transparent !tw-border-[--color-border-primary]':
            type === 'outlineNeutral',
        },
        { 'tw-w-full': fullWidth },
      )}
    >
      {iconPosition !== 'end' && !loading && ReactIconClass && (
        <ReactIconClass className="tw-shrink-0" size={16} />
      )}
      {label || null}
      {iconPosition === 'end' && !loading && ReactIconClass && (
        <ReactIconClass className="tw-shrink-0" size={16} />
      )}
    </AntDesignButton>
  </Tooltip>
  ),
);

Button.displayName = 'Button';

export default Button;
